/**
 * CorporateCarbonMonetisation — /corporate/monetisation
 * Rule-based carbon monetisation scan for corporate emissions and initiatives
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, DollarSign, Building2, ChevronDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const PRIORITY_COLORS = {
  high:   "bg-emerald-50 border-emerald-200 text-emerald-700",
  medium: "bg-amber-50 border-amber-200 text-amber-700",
  low:    "bg-slate-50 border-slate-200 text-slate-600",
};

const PATHWAY_COLORS = {
  insetting: "bg-teal-50 text-teal-700 border-teal-200",
  poa:       "bg-blue-50 text-blue-700 border-blue-200",
  vcm:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  article6:  "bg-amber-50 text-amber-700 border-amber-200",
};

function OpportunityCard({ opp, onCreateProject }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const rec = opp.pathway_assessment?.recommended;

  const handleCreate = async () => {
    const name = prompt("Project name:", `${opp.label} — ${new Date().getFullYear()}`);
    if (!name) return;
    setCreating(true);
    try {
      const res = await client.post("/corporate/monetisation/project", {
        opportunity_category: opp.category,
        project_name: name,
        target_annual_reduction_tco2e: opp.indicative_annual_reduction_tco2e,
        pathway: rec?.pathway,
      });
      toast.success(`Project "${name}" created — navigate to Projects workspace.`);
      onCreateProject?.(res.data);
    } catch { toast.error("Failed to create project."); }
    setCreating(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-3"
      data-testid={`opportunity-${opp.category}`}>
      <button className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${PRIORITY_COLORS[opp.priority] || PRIORITY_COLORS.low}`}>
            {opp.priority?.toUpperCase()}
          </span>
          <div className="text-left">
            <p className="text-sm font-semibold text-slate-900">{opp.label}</p>
            <p className="text-xs text-slate-400">{opp.description?.slice(0,70)}...</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right mr-2">
            <p className="text-sm font-bold text-teal-700">{opp.indicative_annual_reduction_tco2e?.toFixed(0)}</p>
            <p className="text-xs text-slate-400">tCO2e/yr</p>
          </div>
          <ChevronDown size={13} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {/* Readiness strip — always visible */}
      <div className={`flex items-center justify-between px-5 py-2 border-t border-slate-50 ${
        opp.readiness?.status === "ready_for_structuring" ? "bg-emerald-50" :
        opp.readiness?.status === "requires_improvement"  ? "bg-amber-50"   : "bg-red-50"
      }`}>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
            opp.readiness?.status === "ready_for_structuring" ? "bg-emerald-100 border-emerald-200 text-emerald-700" :
            opp.readiness?.status === "requires_improvement"  ? "bg-amber-100 border-amber-200 text-amber-700"       :
            "bg-red-100 border-red-200 text-red-600"
          }`} data-testid={`readiness-${opp.category}`}>
            {opp.readiness?.label || "Assessing..."}
          </span>
          {opp.readiness?.status === "ready_for_structuring" && (
            <button className="text-xs font-bold text-emerald-700 hover:underline"
              onClick={(e) => { e.stopPropagation(); navigate("/corporate/carbon-structuring"); }}>
              → Go to Carbon Project Structuring
            </button>
          )}
          {opp.readiness?.issues?.length > 0 && (
            <span className="text-xs text-amber-600">{opp.readiness.issues[0].slice(0,50)}...</span>
          )}
        </div>
        <span className="text-xs text-slate-400">Priority score: {opp.priority_score?.toFixed(0)}/100</span>
      </div>
      {open && (
        <div className="px-5 pb-4 border-t border-slate-50">
          <div className="grid grid-cols-3 gap-2 mt-3 mb-3">
            <div className="bg-slate-50 rounded-xl px-3 py-2 text-center">
              <p className="text-xs text-slate-400">Additionality</p>
              <p className="text-xs font-bold text-slate-700 capitalize">{opp.additionality}</p>
            </div>
            <div className="bg-slate-50 rounded-xl px-3 py-2 text-center">
              <p className="text-xs text-slate-400">MRV Complexity</p>
              <p className="text-xs font-bold text-slate-700 capitalize">{opp.mrv_readiness}</p>
            </div>
            <div className="bg-slate-50 rounded-xl px-3 py-2 text-center">
              <p className="text-xs text-slate-400">Base tCO2e/mo</p>
              <p className="text-xs font-bold text-slate-700">{opp.monthly_base_tco2e?.toFixed(2)}</p>
            </div>
          </div>

          {/* Pathway */}
          {rec && (
            <div className="mb-3">
              <p className="text-xs font-bold text-slate-500 mb-1.5">Recommended Pathway</p>
              <div className={`rounded-xl px-3 py-2 border ${PATHWAY_COLORS[rec.pathway] || ""}`}>
                <p className="text-xs font-bold">{rec.label}</p>
                <p className="text-xs mt-0.5">{rec.description}</p>
                <p className="text-xs mt-1 opacity-70">Standards: {rec.standards?.join(", ")}</p>
              </div>
              {opp.pathway_assessment?.alternatives?.map((alt, i) => (
                <div key={i} className={`rounded-xl px-3 py-1.5 border mt-1.5 ${PATHWAY_COLORS[alt.pathway] || ""} opacity-70`}>
                  <p className="text-xs font-bold">{alt.label} <span className="font-normal opacity-70">— Alternative</span></p>
                </div>
              ))}
            </div>
          )}

          {/* Evidence required */}
          {opp.evidence_required?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-bold text-slate-500 mb-1">Evidence Required</p>
              {opp.evidence_required.map((e, i) => (
                <p key={i} className="text-xs text-slate-400">• {e}</p>
              ))}
            </div>
          )}

          <p className="text-xs text-slate-400 italic mb-3">{opp.source_note}</p>

          <button onClick={handleCreate} disabled={creating}
            className="px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50">
            {creating ? "Creating..." : "Convert to Carbon Project →"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function CorporateCarbonMonetisation() {
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    client.get("/corporate/monetisation/scan")
      .then(r => { setData(r.data); })
      .catch(e => setError(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="CORPORATE WORKSPACE"
        title="Carbon Monetisation"
        subtitle="Rule-based opportunity scan — deterministic, auditable, reproducible"
        backUrl="/corporate"
        backLabel="Corporate Dashboard"
      />

        {error || data?.scan_status === "no_data" ? (
          <SectionCard>
            <div className="py-8 text-center">
              <AlertTriangle size={24} className="text-amber-500 mx-auto mb-3" />
              <p className="text-sm font-semibold text-amber-700">Emissions data required</p>
              <p className="text-xs text-amber-600 mt-1 mb-4">Calculate at least 3 months of emissions data to enable monetisation scan.</p>
              <CTAButton variant="action" onClick={() => navigate("/corporate/monthly/inputs")}>Enter Monthly Data →</CTAButton>
            </div>
          </SectionCard>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <MetricCard label="Opportunities"  value={data?.total_opportunities || 0}                                              sub="Identified"       color="text-teal-700" />
              <MetricCard label="High Priority"  value={data?.high_priority_count || 0}                                             sub="Immediate action"  color="text-emerald-700" />
              <MetricCard label="Total Potential" value={`${(data?.total_indicative_annual_reduction_tco2e || 0).toFixed(0)} tCO₂e`} sub="Annual reduction"  color="text-blue-700" />
            </div>

            {/* Opportunities */}
            {(data?.opportunities || []).map((opp) => (
              <OpportunityCard key={opp.category} opp={opp}
                onCreateProject={() => navigate("/project/assessment")} />
            ))}

            <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-4">
              <p className="text-xs text-slate-400 italic">{data?.disclaimer}</p>
            </div>
          </>
        )}
    </div>
  );
}
